/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/DeviceSession.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed         |
 * |---|----------------------|-------------------|------------------------|----------------------|
 * | 1 | 2026-06-15 (Step 3)  | Claude Sonnet 4.6 | DeviceSession (Step 3) | Created from scratch |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (DeviceSession class, DeviceState type)
 * - apps/studio/src/device/DeviceSession.test.ts  (unit tests via injected deps)
 * - (Phase 3) apps/studio/src/views/RecorderView.tsx  (adb facade)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - DeviceState      (discriminated union — all possible session states)
 * - DeviceSessionDeps (injectable deps for testing — manager, credStore, auth, makeAdb)
 * - DeviceSession    (class: connect(), disconnect(), subscribe(), get state())
 * - re-exports ConnectionError, ConnectionErrorKind from ConnectionDoctor
 *
 * CALLER → CALLEE MAP
 * --------------------
 * App.tsx → new DeviceSession() → session.connect()
 *                               ↓
 *   deps.manager.requestDevice()              (@yume-chan/adb-daemon-webusb)
 *         ↓
 *   device.connect()                          (claims USB interface)
 *         ↓
 *   deps.authenticate({ serial, connection, credentialStore })
 *                                             (AdbDaemonTransport.authenticate)
 *         ↓
 *   deps.makeAdb(transport)                   (new Adb(transport))
 *         ↓
 *   state = { status:"connected", adb, serial, deviceName }
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. State machine: idle → requesting → connecting → authenticating → connected
 *                   Any state → error (on exception)
 *                   connected → disconnected (when adb.disconnected resolves)
 * 2. All deps are injectable for testing. In production defaults apply:
 *    - manager: AdbDaemonWebUsbDeviceManager.BROWSER (undefined when no WebUSB)
 *    - makeCredentialStore: () => new AdbWebCredentialStore("mimiq")
 *    - authenticate: AdbDaemonTransport.authenticate
 *    - makeAdb: (transport) => new Adb(transport)
 * 3. Subscribers receive the new state synchronously on each transition.
 *    No async queue; state is always consistent when listener is called.
 * 4. adb.disconnected is a Promise that resolves when the transport drops.
 *    We wire it to auto-transition to "disconnected" state.
 * 5. NEVER call connect() in a "connected" state — it's a no-op guard.
 *    Callers should disconnect() first if they want to reconnect.
 * 6. The `authenticate` dependency accepts the full AdbDaemonAuthenticationOptions
 *    shape, NOT just { serial, connection, credentialStore }, so callers can pass
 *    extended options (features, readTimeLimit etc.) in future phases.
 *
 * ISSUES FACED / KNOWN GOTCHAS
 * -----------------------------
 * - AdbDaemonWebUsbDeviceManager.BROWSER is undefined in Node.js — always check
 *   before calling requestDevice(). Tests must inject a mock manager.
 * - requestDevice() returns `undefined` (not throws) when user cancels picker.
 *   We treat that as a clean "idle" return, not an error.
 * - DeviceBusyError is thrown by Tango when another process holds the ADB interface.
 *   It is NOT a DOMException; check error.constructor.name in ConnectionDoctor.
 *
 * =============================================================================
 */

import { Adb, AdbDaemonTransport } from "@yume-chan/adb";
import type { AdbDaemonAuthenticationOptions } from "@yume-chan/adb";
import { AdbDaemonWebUsbDeviceManager } from "@yume-chan/adb-daemon-webusb";
import AdbWebCredentialStore from "@yume-chan/adb-credential-web";
import { classifyError } from "./ConnectionDoctor.js";

export type { ConnectionError, ConnectionErrorKind } from "./ConnectionDoctor.js";
import type { ConnectionError } from "./ConnectionDoctor.js";

/* ── State shape ──────────────────────────────────────────────────────────── */

export type DeviceState =
  | { status: "idle" }
  | { status: "requesting" }
  | { status: "connecting"; serial: string }
  | { status: "authenticating"; serial: string }
  | { status: "connected"; adb: Adb; serial: string; deviceName: string }
  | { status: "error"; error: ConnectionError }
  | { status: "disconnected" };

export type StateListener = (state: DeviceState) => void;

/* ── Injectable dependencies ──────────────────────────────────────────────── */

export interface DeviceSessionDeps {
  /** WebUSB device manager. Defaults to AdbDaemonWebUsbDeviceManager.BROWSER.
   *  Inject a mock in tests; will be undefined in non-Chromium browsers. */
  manager?: AdbDaemonWebUsbDeviceManager | undefined;

  /** Credential store factory. Defaults to AdbWebCredentialStore("mimiq"). */
  makeCredentialStore?: () => AdbWebCredentialStore;

  /** ADB authentication. Defaults to AdbDaemonTransport.authenticate. */
  authenticate?: (opts: AdbDaemonAuthenticationOptions) => Promise<AdbDaemonTransport>;

  /** Adb facade factory. Defaults to (t) => new Adb(t). Injectable for tests. */
  makeAdb?: (transport: AdbDaemonTransport) => Adb;
}

/* ── DeviceSession ────────────────────────────────────────────────────────── */

export class DeviceSession {
  #state: DeviceState = { status: "idle" };
  #listeners = new Set<StateListener>();
  #adb: Adb | null = null;

  readonly #manager: AdbDaemonWebUsbDeviceManager | undefined;
  readonly #makeCredentialStore: () => AdbWebCredentialStore;
  readonly #authenticate: (opts: AdbDaemonAuthenticationOptions) => Promise<AdbDaemonTransport>;
  readonly #makeAdb: (transport: AdbDaemonTransport) => Adb;

  constructor(deps: DeviceSessionDeps = {}) {
    this.#manager = "manager" in deps ? deps.manager : AdbDaemonWebUsbDeviceManager.BROWSER;
    this.#makeCredentialStore =
      deps.makeCredentialStore ?? (() => new AdbWebCredentialStore("mimiq"));
    this.#authenticate =
      deps.authenticate ?? ((opts) => AdbDaemonTransport.authenticate(opts));
    this.#makeAdb = deps.makeAdb ?? ((t) => new Adb(t));
  }

  /* ── State accessors ──────────────────────────────────────────────────── */

  get state(): DeviceState {
    return this.#state;
  }

  /**
   * Subscribe to state changes. The listener is called immediately with the
   * current state, then on every subsequent transition.
   * Returns an unsubscribe function.
   */
  subscribe(listener: StateListener): () => void {
    this.#listeners.add(listener);
    listener(this.#state);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /* ── Internal ─────────────────────────────────────────────────────────── */

  #setState(next: DeviceState): void {
    this.#state = next;
    for (const listener of this.#listeners) {
      listener(next);
    }
  }

  /* ── Public API ───────────────────────────────────────────────────────── */

  /**
   * Run the full WebUSB → ADB connection flow.
   *
   * State transitions:
   *   idle/error/disconnected → requesting → connecting → authenticating → connected
   *   On any exception → error
   *   If user cancelled picker → back to idle
   */
  async connect(): Promise<void> {
    const { status } = this.#state;
    if (
      status === "requesting" ||
      status === "connecting" ||
      status === "authenticating" ||
      status === "connected"
    ) {
      return; // Already in-flight or connected
    }

    // Guard: WebUSB not available (Firefox, Safari, or Chrome policy block)
    if (!this.#manager) {
      this.#setState({
        status: "error",
        error: classifyError(new Error("NO_WEBUSB")),
      });
      return;
    }

    try {
      // ── Step 1: Device picker ──────────────────────────────────────────
      this.#setState({ status: "requesting" });
      const device = await this.#manager.requestDevice();

      if (!device) {
        // User dismissed the picker — not an error, just go back to idle
        this.#setState({ status: "idle" });
        return;
      }

      // ── Step 2: Claim USB interface ────────────────────────────────────
      this.#setState({ status: "connecting", serial: device.serial });
      const connection = await device.connect();

      // ── Step 3: ADB handshake + RSA auth ──────────────────────────────
      this.#setState({ status: "authenticating", serial: device.serial });
      const credentialStore = this.#makeCredentialStore();
      const transport = await this.#authenticate({
        serial: device.serial,
        connection,
        credentialStore,
      });

      // ── Step 4: Build Adb facade ───────────────────────────────────────
      const adb = this.#makeAdb(transport);
      this.#adb = adb;
      this.#setState({
        status: "connected",
        adb,
        serial: device.serial,
        deviceName: device.name ?? device.serial,
      });

      // Auto-transition when the physical USB connection drops
      adb.disconnected.then(() => {
        if (this.#state.status === "connected") {
          this.#adb = null;
          this.#setState({ status: "disconnected" });
        }
      }).catch(() => {
        /* Transport closed with error — already handled by disconnect() or re-connect */
      });
    } catch (err) {
      this.#adb = null;
      this.#setState({
        status: "error",
        error: classifyError(err),
      });
    }
  }

  /**
   * Gracefully close the ADB transport and reset state to "disconnected".
   */
  async disconnect(): Promise<void> {
    if (this.#adb) {
      await this.#adb.close().catch(() => {
        /* Ignore close errors — we're cleaning up */
      });
      this.#adb = null;
    }
    const { status } = this.#state;
    if (status !== "idle" && status !== "disconnected") {
      this.#setState({ status: "disconnected" });
    }
  }
}
